import { motion } from "framer-motion";
import { Smartphone, Shield, MapPin, Users, ExternalLink, Download } from "lucide-react";

const PROJECT_URL = "https://www.asanimation61.com/djmatch";
const APP_DOWNLOAD_URL =
  "https://drive.google.com/file/d/1vq5UqAPDJWn0lDx5uJSQBWc9ZKfp_hwC/view?usp=sharing";
const APP_LOGO =
  "https://static.wixstatic.com/media/53b2bd_82d24c1e8232477ab96cdd2ba748d39f~mv2.png/v1/fill/w_459,h_449,al_c,q_85,usm_0.66_1.00_0.01,enc_avif,quality_auto/L%E2%80%99Annuaire%20des%20DJs%20Pros%20En%20France%20(2).png";
const APP_SCREEN =
  "https://static.wixstatic.com/media/53b2bd_0390a36a46574e6fa61e80da92755139~mv2.png/v1/fill/w_420,h_627,al_c,lg_1,q_85,enc_avif,quality_auto/53b2bd_0390a36a46574e6fa61e80da92755139~mv2.png";

const FEATURES = [
  { icon: <Shield size={18} />, title: "Profils vérifiés", text: "DJs déclarés, fiables et expérimentés." },
  { icon: <MapPin size={18} />, title: "Partout en France", text: "Trouvez un DJ pro près de chez vous." },
  { icon: <Users size={18} />, title: "Mariages & événements", text: "Pour particuliers et professionnels." },
];

export const DjMatchSection = () => {
  return (
    <section
      id="djmatch"
      data-testid="djmatch-section"
      className="relative py-24 md:py-32 px-6 md:px-12 lg:px-24 bg-black border-t border-white/5 overflow-hidden"
    >
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,rgba(45,108,255,0.18),transparent_60%)]" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(212,175,55,0.10),transparent_60%)]" />

      <div className="relative z-10 max-w-7xl mx-auto grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.7 }}
          className="lg:col-span-7 order-2 lg:order-1"
        >
          <span className="eyebrow inline-flex items-center gap-2">
            <Smartphone size={13} /> Mon application
          </span>

          <div className="mt-3 flex items-center gap-4 flex-wrap">
            <img
              src={APP_LOGO}
              alt="DJ Match France"
              className="w-16 h-16 md:w-20 md:h-20 rounded-2xl object-cover border border-white/10"
              loading="lazy"
            />
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight">
              <span className="gold-text-gradient">DJ Match</span> France
            </h2>
          </div>

          <p className="mt-6 text-zinc-300 leading-relaxed text-base md:text-lg">
            Pour défendre la profession, j'ai créé{" "}
            <span className="text-white font-semibold">
              le premier annuaire des DJs professionnels en France.
            </span>{" "}
            Une application qui met en relation clients et DJs vérifiés, déclarés et
            expérimentés — partout en France, pour mariages, anniversaires et événements.
          </p>

          <div className="mt-8 grid sm:grid-cols-3 gap-3">
            {FEATURES.map((f) => (
              <div
                key={f.title}
                className="glass rounded-xl p-4"
                data-testid={`djmatch-feature-${f.title.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <div className="inline-flex items-center justify-center w-9 h-9 rounded-lg bg-[#2D6CFF]/15 text-[#4A86FF] mb-3">
                  {f.icon}
                </div>
                <div className="text-sm font-semibold text-white">{f.title}</div>
                <div className="mt-1 text-xs text-zinc-400 leading-snug">{f.text}</div>
              </div>
            ))}
          </div>

          <div className="mt-9 flex flex-wrap gap-3">
            <a
              href={APP_DOWNLOAD_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="djmatch-download-btn"
              className="btn-gold rounded-full px-7 py-4 text-sm md:text-base inline-flex items-center gap-2"
            >
              <Download size={16} /> Télécharger l'app
            </a>
            <a
              href={PROJECT_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="djmatch-discover-btn"
              className="btn-outline-gold rounded-full px-7 py-4 text-sm md:text-base inline-flex items-center gap-2"
            >
              Découvrir le projet <ExternalLink size={15} />
            </a>
          </div>

          <p className="mt-6 text-xs text-zinc-500 max-w-xl">
            Un projet engagé pour valoriser et protéger la profession de DJ professionnel
            en France. Vu et soutenu par la communauté Coulisse DJ.
          </p>
        </motion.div>

        {/* Phone mockup */}
        <motion.div
          initial={{ opacity: 0, scale: 0.92 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.8, delay: 0.1, ease: [0.2, 0.8, 0.2, 1] }}
          className="lg:col-span-5 order-1 lg:order-2 relative flex justify-center"
        >
          {/* Glow background */}
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-[80%] h-[80%] rounded-full bg-gradient-to-br from-[#2D6CFF]/30 via-[#D4AF37]/20 to-transparent blur-3xl" />
          </div>

          <div className="relative">
            <img
              src={APP_SCREEN}
              alt="DJ Match France – capture d'écran"
              className="relative max-w-[280px] md:max-w-[320px] lg:max-w-[360px] w-full h-auto drop-shadow-[0_30px_60px_rgba(45,108,255,0.35)]"
              loading="lazy"
            />
            {/* Floating badge */}
            <div className="absolute -top-4 -right-4 md:-right-8 glass rounded-2xl px-4 py-3 flex items-center gap-2 hidden sm:flex">
              <div className="w-2.5 h-2.5 rounded-full bg-[#1DB954] animate-pulse" />
              <div className="text-xs">
                <div className="text-white font-semibold">Disponible</div>
                <div className="text-[10px] text-zinc-400 uppercase tracking-wider">Android</div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
